// [claude-code 2026-05-03] S38-T5: Provider Dropdown v2 — 3 providers, RECOMMENDED badge, flat palette, API key hints.
import { useCallback, useState, useRef, useEffect } from "react";
import { useSettings } from "../../contexts/SettingsContext";
import { ProviderIcon } from "./ProviderIcon";
import {
  PROVIDERS,
  normalizeHarperProvider,
  readInteractiveChatProvider,
  readStoredHarperProvider,
  writeActiveHarperProvider,
  providerStatusKey,
  providerDisplayName,
  providerHasStatusIssue,
  providerSubtext,
  type HarperProvider,
} from "./provider-options";

export type { HarperProvider } from "./provider-options";

function initialProvider(defaultProvider?: string | null): HarperProvider {
  return readStoredHarperProvider(defaultProvider);
}

function hasCachedKey(storageKey: string) {
  try {
    const value = localStorage.getItem(storageKey);
    return value === "set" || value === "oauth";
  } catch {
    return false;
  }
}

function missingProviderHint(provider: HarperProvider): string | null {
  if (hasCachedKey(providerStatusKey(provider))) return null;
  if (provider === "openai") return "Connect OpenAI";
  if (provider === "nous") return "Connect Nous";
  return "Set API key";
}

/* ─────────────────────────────────────────────────────── Dot Color ── */

function getDotColor(provider: HarperProvider): string {
  return hasCachedKey(providerStatusKey(provider)) ? "#22c55e" : "#ca8a04";
}

/* ─────────────────────────────────────────────────────── Hook ── */

export function useHarperProvider() {
  const { defaultChatProvider } = useSettings();
  const [provider, setProviderState] = useState<HarperProvider>(() => {
    return initialProvider(defaultChatProvider);
  });

  useEffect(() => {
    const normalized = normalizeHarperProvider(defaultChatProvider);
    setProviderState(normalized);
  }, [defaultChatProvider]);

  const setProvider = (p: HarperProvider) => {
    setProviderState(p);
    writeActiveHarperProvider(p);
  };

  return { provider, setProvider };
}

export function useInteractiveChatProvider() {
  const [provider, setProviderState] = useState<HarperProvider>(
    readInteractiveChatProvider,
  );
  useEffect(() => {
    const refresh = () => setProviderState(readInteractiveChatProvider());
    window.addEventListener("fintheon:harper-provider-active-changed", refresh);
    window.addEventListener(
      "fintheon:harper-provider-default-changed",
      refresh,
    );
    return () => {
      window.removeEventListener(
        "fintheon:harper-provider-active-changed",
        refresh,
      );
      window.removeEventListener(
        "fintheon:harper-provider-default-changed",
        refresh,
      );
    };
  }, []);
  const setProvider = useCallback((next: HarperProvider) => {
    writeActiveHarperProvider(next);
    setProviderState(next);
  }, []);

  return { provider, setProvider };
}

/* ─────────────────────────────────────────────────────── Component ── */

interface ProviderDropdownProps {
  provider: HarperProvider;
  onChange: (p: HarperProvider) => void;
  compact?: boolean;
}

export function ProviderDropdown({
  provider,
  onChange,
  compact,
}: ProviderDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const current = PROVIDERS.find((p) => p.id === provider) ?? PROVIDERS[0];
  const currentName = providerDisplayName(current);
  const currentSubtext = providerSubtext(current);
  const dotColor = getDotColor(provider);

  const keyMissingHint = providerHasStatusIssue(provider)
    ? currentSubtext
    : missingProviderHint(provider);

  const openSettings = () => {
    window.dispatchEvent(new CustomEvent("fintheon:open-settings-api"));
  };

  return (
    <div ref={ref} className="relative flex items-center">
      {/* ── Trigger row ── */}
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-haspopup="listbox"
          className={`flex items-center gap-1.5 rounded-lg border-0 bg-transparent transition-colors hover:text-[var(--fintheon-accent)] ${
            compact ? "px-1.5" : "px-2"
          }`}
          style={{
            color: "var(--fintheon-accent)",
            height: "28px",
          }}
          title={`Provider: ${currentName} (${currentSubtext})`}
        >
          {/* colored status dot */}
          <span
            className="inline-block w-2 h-2 rounded-full shrink-0"
            style={{ backgroundColor: dotColor }}
          />
          <ProviderIcon icon={current.icon} className="h-3.5 w-3.5 shrink-0" />

          {!compact && (
            <>
              <span
                style={{
                  fontSize: "11px",
                  color: "rgba(240, 234, 214, 0.8)",
                }}
              >
                {currentName}
              </span>
              <span
                style={{
                  fontSize: "10px",
                  color: "rgba(240, 234, 214, 0.45)",
                }}
              >
                {currentSubtext}
              </span>
            </>
          )}
        </button>

        {/* inline key-missing hint next to trigger */}
        {!compact && keyMissingHint && (
          <span
            role="button"
            tabIndex={0}
            className="max-w-[140px] truncate cursor-pointer"
            style={{ fontSize: "10px", color: "rgba(199, 159, 74, 0.7)" }}
            onClick={openSettings}
            onKeyDown={(e) => {
              if (e.key === "Enter") openSettings();
            }}
          >
            {keyMissingHint}
          </span>
        )}
      </div>

      {/* ── Expanded dropdown ── */}
      {open && (
        <div
          role="listbox"
          className="absolute bottom-full right-0 mb-1 min-w-[220px] rounded-2xl border overflow-hidden z-50"
          style={{
            backgroundColor: "#050402",
            borderColor: "rgba(199, 159, 74, 0.3)",
            boxShadow: "0 4px 24px rgba(0, 0, 0, 0.6)",
          }}
        >
          {PROVIDERS.map((p, i) => {
            const active = p.id === provider;
            const pDotColor = getDotColor(p.id);
            const displayName = providerDisplayName(p);
            const subtext = providerSubtext(p);
            const hasIssue = providerHasStatusIssue(p.id);

            return (
              <button
                key={p.id}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => {
                  onChange(p.id);
                  setOpen(false);
                }}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors ${
                  i < PROVIDERS.length - 1
                    ? "border-b border-[rgba(199,159,74,0.08)]"
                    : ""
                } ${
                  active
                    ? "bg-[rgba(199,159,74,0.1)] text-[#c79f4a]"
                    : "text-[rgba(240,234,214,0.6)] hover:bg-[rgba(199,159,74,0.05)] hover:text-[rgba(240,234,214,0.8)]"
                }`}
              >
                <ProviderIcon
                  icon={p.icon}
                  className="h-3.5 w-3.5 shrink-0 text-[var(--fintheon-accent)]"
                />

                <div className="flex flex-col flex-1 min-w-0">
                  {/* label row */}
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-medium">{displayName}</span>
                  </div>

                  {/* model subtext or provider error */}
                  <span
                    style={{
                      fontSize: "9px",
                      color: hasIssue
                        ? "rgba(248, 113, 113, 0.78)"
                        : "currentColor",
                      opacity: hasIssue ? 1 : 0.6,
                    }}
                  >
                    {subtext}
                  </span>
                </div>

                {/* status dot (right-aligned) */}
                <span
                  className="inline-block w-2 h-2 rounded-full shrink-0"
                  style={{ backgroundColor: pDotColor }}
                />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
